import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { DeviceARPDataType } from '../../../../types/buildingTypes';
import { formatMacAddress } from '../../../../utils/macFormatter';
import { isDescriptionValid } from '../../../../hooks/useBulkDhcpReservationForm';
import dialogStyles from '../../common/dialogStyles';

interface BulkDhcpReservationRowProps {
  row: DeviceARPDataType;
  description: string;
  touched: boolean;
  submitAttempted: boolean;
  isDuplicate: boolean;
  allowedInputCharacters: RegExp;
  maxDescriptionLength: number;
  onDescriptionChange: (ipAddress: string, value: string) => void;
  onBlur: (ipAddress: string) => void;
}

const BulkDhcpReservationRow: React.FC<BulkDhcpReservationRowProps> = ({
  row,
  description,
  touched,
  submitAttempted,
  isDuplicate,
  allowedInputCharacters,
  maxDescriptionLength,
  onDescriptionChange,
  onBlur,
}) => {
  const macAddress = formatMacAddress(row.mac_address);
  const isValid = isDescriptionValid(description);
  const showValidationError = (!isValid && (touched || submitAttempted)) || isDuplicate;

  const getFieldHelperText = () => {
    if (showValidationError) {
      if (description.trim().length === 0) {
        return 'DHCP reservation description cannot be empty.';
      }
      if (!allowedInputCharacters.test(description)) {
        return 'Only alphanumeric characters, hyphens (-), and underscores (_) are allowed. No spaces or symbols.';
      }
      if (isDuplicate) {
        return 'This description is used by another selected row. Descriptions must be unique.';
      }
    }

    return `${description.length} / ${maxDescriptionLength}`;
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={dialogStyles.reservationInfoStyles}>
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold">
            MAC:
          </Typography>{' '}
          {macAddress}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold">
            IP:
          </Typography>{' '}
          {row.ip_address}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold">
            Interface:
          </Typography>{' '}
          {row.interface}
        </Typography>
      </Box>
      <TextField
        label="DHCP reservation description"
        value={description.toLowerCase()}
        onChange={(e) => onDescriptionChange(row.ip_address, e.target.value)}
        onBlur={() => onBlur(row.ip_address)}
        required
        fullWidth
        error={showValidationError}
        helperText={getFieldHelperText()}
        multiline
        maxRows={3}
        inputProps={{ maxLength: maxDescriptionLength }}
        sx={dialogStyles.dialogContentFormTextField}
        placeholder="Enter description"
      />
    </Box>
  );
};

export default BulkDhcpReservationRow;
